import { ValidationMessages } from "./errorMessages";

type ValidatorFn = (value: any, allValues?: Record<string, any>, rowIndex?: number) => string | null;

export const validators = {
  required: (message?: string): ValidatorFn => (value) => {
    if (
      value === undefined ||
      value === null ||
      (typeof value === "string" && !value.trim()) ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return message || ValidationMessages.required;
    }
    return null;
  },

  requiredSelection: (message?: string): ValidatorFn => (value) => {
    if (value === undefined || value === null || value === "") {
      return message || ValidationMessages.requiredSelection;
    }
    return null;
  },

  // Use a ReDoS-resistant email check implemented via code logic.
  // This avoids backtracking risks.
  email: (message?: string): ValidatorFn => (value: string) => {
    const isValidDomainLabel = (label: string): boolean => {
      if (!label || label.length > 63) return false;
      if (label.startsWith('-') || label.endsWith('-')) return false;
      
      for (let i = 0; i < label.length; i++) {
        const code = label.codePointAt(i);
        if (code === undefined) return false;
        const isAlphaNum = (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
        const isHyphen = code === 45;
        if (!isAlphaNum && !isHyphen) return false;
      }
      return true;
    };

    const isValidEmail = (v: string): boolean => {
      if (!v) return false;
      if (v.length > 254) return false;
      
      const atIndex = v.indexOf('@');
      if (atIndex <= 0) return false;
      if (v.includes('@', atIndex + 1)) return false; // only one @
      
      const local = v.slice(0, atIndex);
      const domain = v.slice(atIndex + 1);
      
      if (!local || !domain) return false;
      if (local.length > 64 || domain.length > 255) return false;
      if (!domain.includes('.')) return false; // must have dot in domain
      if (/\s/.test(v)) return false; // no whitespace
      if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
      
      return domain.split('.').every(isValidDomainLabel);
    };

    if (!value || isValidEmail(value)) return null;
    return message || ValidationMessages.email;
  },

  minLength: (min: number, message?: string): ValidatorFn => (value: string) => {
    if (!value || value.length >= min) return null;
    return message || ValidationMessages.minLength(min);
  },

  maxLength: (max: number, message?: string): ValidatorFn => (value: string) => {
    if (!value || value.length <= max) return null;
    return message || ValidationMessages.maxLength(max);
  },

  minValue: (min: number, message?: string): ValidatorFn => (value: number) => {
    if (typeof value === "number" && value >= min) return null;
    return message || ValidationMessages.minValue(min);
  },

  maxValue: (max: number, message?: string): ValidatorFn => (value: number) => {
    if (typeof value === "number" && value <= max) return null;
    return message || ValidationMessages.maxValue(max);
  },

  pattern: (regex: RegExp, message?: string): ValidatorFn => (value: string) => {
    if (!value || regex.test(value)) return null;
    return message || ValidationMessages.pattern;
  },

  custom: (fn: (value: any, allValues?: Record<string, any>) => string | null, message?: string): ValidatorFn =>
    (value, allValues) => {
      const result = fn(value, allValues);
      return result ? message || result : null;
    },

  compose: (...rules: ValidatorFn[]): ValidatorFn => (value, allValues, rowIndex) => {
    for (const rule of rules) {
      const error = rule(value, allValues, rowIndex);
      if (error) return error;
    }
    return null;
  },

  emailOrSmsRequired: (field: "email" | "sms", message?: string): ValidatorFn => (value, allValues) => {
    const email = allValues?.email?.trim();
    const sms = allValues?.sms?.trim();

    if (!email && !sms) {
      return message || "Either Email or SMS number is required.";
    }

    if (email && sms) {
      return message || "Please enter only one contact: remove Email or SMS.";
    }

    return null;
  },

  applyToFirstRowOnly: (rules: ValidatorFn[]): ValidatorFn[] => {
    return rules.map(
      (rule) =>
        (value: any, allValues?: Record<string, any>, rowIndex?: number) => {
          if (rowIndex !== 0) {
            return null;
          }
          return rule(value, allValues, rowIndex);
        }
    );
  },
  // Apply these rules only to rows after the first (i.e. recipient rows),
  // so the profile-name-only first row is not forced to have recipient fields.
  applyToNonFirstRowOnly: (rules: ValidatorFn[]): ValidatorFn[] => {
    return rules.map(
      (rule) =>
        (value: any, allValues?: Record<string, any>, rowIndex?: number) => {
          if (rowIndex === 0) {
            return null;
          }
          return rule(value, allValues, rowIndex);
        }
    );
  },
  // Apply these rules only to rows after the first, unless it's a bureau-only recipient (which also doesn't need the profile-name-only first row fields).
  applyToNonFirstRowUnlessBureauOnly: (rules: ValidatorFn[]): ValidatorFn[] => {
    return rules.map(
      (rule) =>
        (value: any, allValues?: Record<string, any>, rowIndex?: number) => {
          if (rowIndex === 0) {
            return null;
          }
          const isBureau = allValues?.isBureauRecipient === true;
          const bureauType = allValues?.bureauContactType;
          if (isBureau && bureauType === 'bureau') {
            return null;
          }
          return rule(value, allValues, rowIndex);
        }
    );
  },
};
